// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './index.module.scss';
import { Modal } from 'components/atoms/Modal';
import { useTranslation } from 'react-i18next';
import { IconClose } from 'components/atoms/icons/IconClose';
import { CategoryItem, ItemType, TareItem } from 'util/Types';
import { getUnitLabel, mergeClasses, normalizeNumber } from 'util/commons';
import BigNumber from 'bignumber.js';
import { useLayoutEffect, useState } from 'react';
import _debounce from 'lodash/debounce';
import IconBuilding from 'components/atoms/icons/IconBuilding';
import Popover from 'components/atoms/Popover';

export type EmissionConfirmModalProps = {
  isOpen: boolean;
  emissionType: CategoryItem;
  measuredWeight: string;
  quantity: string;
  closeEditModal: () => void;
  onClick: () => void;
  onClickSubmitAndFinish?: () => void;
  tare?: Partial<TareItem>;
  tenantName?: string;
};

export const EmissionConfirmModal = (props: EmissionConfirmModalProps) => {
  const [maxLabelWidth, setMaxLabelWidth] = useState<number>();

  const { t } = useTranslation();
  const {
    isOpen,
    emissionType,
    quantity,
    measuredWeight,
    closeEditModal,
    onClick,
    onClickSubmitAndFinish,
    tare,
    tenantName,
  } = props;

  useLayoutEffect(() => {
    const calculateMaxLabelWidth = () => {
      setMaxLabelWidth(undefined);
      const labelElements = document.querySelectorAll('.confirm-label-cal');

      if (!labelElements.length) {
        return;
      }

      const maxWidth = Math.max(
        ...Array.from(labelElements).map(
          (el) => el.getBoundingClientRect().width
        )
      );

      if (maxWidth > 0) {
        setMaxLabelWidth(maxWidth);
      }
    };

    calculateMaxLabelWidth();
    const debouncedCalculate = _debounce(calculateMaxLabelWidth, 500);

    window.addEventListener('resize', debouncedCalculate);

    return () => {
      window.removeEventListener('resize', debouncedCalculate);
    };
  }, [t, isOpen]);

  const getWeight = (convertKg: string | number | null, quantity: string) => {
    if (!convertKg) return 0;
    const kg = new BigNumber(convertKg);
    return kg.multipliedBy(quantity);
  };

  const confirmContents =
    emissionType.type === ItemType.FIX
      ? [
          {
            label: t('emission_input.label_item_name'),
            contents: `${emissionType.name} ${emissionType.convertKg}kg/${
              emissionType.quantity
            }${getUnitLabel(emissionType.unit)}`,
          },
          {
            label: t('emission_input.label_weight'),
            contents: `${normalizeNumber({
              value: getWeight(emissionType.convertKg, quantity),
              toFixed: false,
            })} kg`,
          },
        ]
      : [
          {
            label: t('emission_input.label_item_name'),
            contents: emissionType.name,
          },
          { label: '', contents: '' },
        ];

  const renderUnitInput = () => {
    if (emissionType.type == ItemType.FIX && emissionType.inputUnit) {
      return emissionType.inputUnit;
    }

    return 'kg';
  };

  const renderLabels = (label: string) => (
    <div
      className="confirm-label-cal"
      style={{
        display: 'inline-block',
        minWidth: maxLabelWidth || 'auto',
      }}
    >
      {label}
    </div>
  );

  return (
    <section
      className={mergeClasses(
        styles.emissionConfirmModal,
        isOpen ? styles.open : styles.closed
      )}
    >
      <Modal>
        <div className={styles.modalContent}>
          <div className={styles.top}>
            <div className={styles.title}>
              {t('emission_input.confirm_title')}
            </div>

            <div className={styles.closeSection}>
              {tenantName && (
                <div className={styles.tenantName}>
                  <div>
                    <IconBuilding />
                  </div>
                  <Popover
                    content={tenantName}
                    containerStyle={{
                      zIndex: '30001',
                    }}
                    positions={['top']}
                    autoGen={{
                      odd: 4,
                      wrapperStyle: {
                        wordBreak: 'break-all',
                      },
                    }}
                  >
                    <span>{tenantName}</span>
                  </Popover>
                </div>
              )}
              <div className={styles.closeIcon} onClick={closeEditModal}>
                <IconClose />
              </div>
            </div>
          </div>

          <div className={styles.body}>
            {tare?.weight && tare.weight !== '0' && (
              <>
                <div className={styles.measuredWeight}>
                  <div className={styles.measuredWeightTitle}>
                    {renderLabels(t('emission_input.measured_weight'))}:
                  </div>
                  <div>
                    {normalizeNumber({
                      value: measuredWeight,
                    })}
                    kg
                  </div>
                </div>

                <div className={styles.tare}>
                  <div className={styles.tareName}>
                    {renderLabels(
                      tare.name || t('emission_input.container_weight')
                    )}
                    :
                  </div>
                  <div>
                    {normalizeNumber({
                      value: tare.weight,
                    })}
                    kg
                  </div>
                </div>
              </>
            )}

            <div className={styles.confirmInfo}>
              {confirmContents.map((item, i) => {
                return (
                  item.label &&
                  item.contents && (
                    <div className={styles.confirmInfoRow} key={i}>
                      <div className={styles.confirmInfoLabel}>
                        {renderLabels(
                          item.label.length > 1 && !item.label.endsWith(':')
                            ? item.label
                            : item.label.slice(0, -1)
                        )}
                        :
                      </div>
                      <div className={styles.confirmInfoContents}>
                        {item.contents}
                      </div>
                    </div>
                  )
                );
              })}
            </div>

            <div className={styles.netWeight}>
              {tare?.weight && tare.weight !== '0' && (
                <div className={styles.netWeightTitle}>
                  {renderLabels(t('emission_input.net_weight'))}:
                </div>
              )}

              <div className={styles.contentUnit}>
                <div className={styles.quantity}>
                  <span>{`${normalizeNumber({
                    value: quantity,
                  })}`}</span>
                </div>
                <div className={styles.weightUnit}>
                  <span>{renderUnitInput()}</span>
                </div>
              </div>
            </div>

            {Number(quantity) < 0 && (
              <div className={styles.alert}>
                {t('emission_input.quantity_exceed_message')}
              </div>
            )}
          </div>
          <div className={styles.confirmBtn}>
            <button
              className={styles.submitButton}
              type="button"
              onClick={Number(quantity) < 0 ? undefined : onClick}
              disabled={Number(quantity) < 0}
            >
              {t('common.button.register_continue')}
            </button>
            <button
              className={styles.finishButton}
              type="button"
              onClick={
                Number(quantity) < 0 ? undefined : onClickSubmitAndFinish
              }
              disabled={Number(quantity) < 0}
            >
              {t('common.button.register_and_finish')}
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
};
